import React from 'react';
import type { FmDataDisplayRule } from '../../types/fm-data-value';

export type { FmDataDisplayRule };

type DataValueKind = 'currency' | 'percent' | 'number' | 'volume' | 'area' | 'date' | 'text' | 'unavailable';

export interface FmDataValueProps {
  /** Display-rule contract driving formatting and presentation. */
  rule: FmDataDisplayRule;
  /** BCP-47 locale for numeric/date formatting. Default 'fi-FI'. */
  locale?: string;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
  /** Localised "not available" label. When omitted, only the em-dash renders. */
  unavailableLabel?: string;
  /** Localised aria-label for the ~ directional qualifier. When omitted, no aria-label set. */
  qualifierAriaLabel?: string;
}

// Formatting helpers

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

interface Formatted {
  text: string;
  unit: string | undefined;
}

type FormatOpts = {
  locale: string;
  precision: number | undefined;
  unit: string | undefined;
  currency: string | undefined;
};

function toNum(value: number | string): number {
  return typeof value === 'string' ? Number(value) : value;
}

function fmtNumeric(value: number, precision: number | undefined, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision ?? 0,
    maximumFractionDigits: precision ?? 0,
  }).format(value);
}

function formatCurrency(value: number | string, opts: FormatOpts): Formatted {
  const text = new Intl.NumberFormat(opts.locale, {
    style: 'currency',
    currency: opts.currency ?? 'EUR',
    minimumFractionDigits: opts.precision ?? 0,
    maximumFractionDigits: opts.precision ?? 0,
  }).format(toNum(value));
  // opts.unit allows per-unit override (e.g. '€/ha'); absent → € already in text
  return { text, unit: opts.unit };
}

function formatDate(value: number | string, opts: FormatOpts): Formatted {
  const dateStr = String(value);
  const date = new Date(dateStr);
  const text = Number.isNaN(date.getTime())
    ? dateStr
    : new Intl.DateTimeFormat(opts.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
  return { text, unit: opts.unit };
}

type NumericKind = 'percent' | 'number' | 'volume' | 'area';

const NUMERIC_DEFAULTS: Record<NumericKind, { precision: number; unit: string | undefined }> = {
  percent: { precision: 1, unit: '%' },
  number:  { precision: 0, unit: undefined },
  volume:  { precision: 1, unit: 'm³/ha' },
  /* ONE hectare precision across the product. Two decimals put "38,40 ha" on the
     same page as "4.8 ha" and "12.6", so a reader had three formats to reconcile
     for the same kind of quantity. A caller that
     genuinely needs more can still pass `precision`. */
  area:    { precision: 1, unit: 'ha' },
};

function formatNumericKind(kind: NumericKind, value: number | string, opts: FormatOpts): Formatted {
  const defaults = NUMERIC_DEFAULTS[kind];
  return {
    text: fmtNumeric(toNum(value), opts.precision ?? defaults.precision, opts.locale),
    unit: opts.unit ?? defaults.unit,
  };
}

function formatValue(
  kind: FmDataDisplayRule['kind'],
  value: number | string,
  opts: FormatOpts,
): Formatted {
  if (kind === 'currency') return formatCurrency(value, opts);
  if (kind === 'date') return formatDate(value, opts);
  if (kind === 'text') return { text: String(value), unit: opts.unit };
  return formatNumericKind(kind as NumericKind, value, opts);
}

function isNumericKind(kind: FmDataDisplayRule['kind']): boolean {
  return kind === 'currency'
    || kind === 'percent'
    || kind === 'number'
    || kind === 'volume'
    || kind === 'area';
}

function signPrefix(
  value: number,
  signDisplay: FmDataDisplayRule['signDisplay'] | undefined,
): string {
  const display = signDisplay ?? 'auto';
  if (display === 'never') return '';
  if (value < 0) return '\u2212';
  if (value > 0 && (display === 'always' || display === 'except-zero')) return '+';
  if (value === 0 && display === 'always') return '+';
  return '';
}

function displayValue(
  kind: FmDataDisplayRule['kind'],
  value: number | string,
): number | string {
  if (!isNumericKind(kind)) return value;
  return Math.abs(toNum(value));
}

// Unavailable helper

function renderUnavailable(
  className: string | undefined,
  dataTestId: string | undefined,
  unavailableLabel: string | undefined,
): React.ReactElement {
  return (
    <span
      className={cx('fm-dv', 'fm-dv--unavailable', className)}
      data-testid={dataTestId ?? 'fm-data-value'}
    >
      <span
        className="fm-dv__unavailable-dash"
        data-testid="fm-data-value-unavailable-dash"
        aria-hidden="true"
      >
        —
      </span>
      <span
        className="fm-dv__unavailable-label"
        data-testid="fm-data-value-unavailable-label"
      >
        {unavailableLabel}
      </span>
    </span>
  );
}

// Component

/**
 * FmDataValue — renders a domain value with consistent formatting and the
 * ux-foundation §8 directional/quantified contract encoded as a CSS flag.
 *
 * - directional=true: wraps value in ~ qualifier (modelled/free data)
 * - kind='unavailable' OR null/undefined value: renders explicit no-data
 *   (em-dash + optional unavailableLabel) and NEVER a fabricated 0 or placeholder.
 * - Dual-theme: reads --fm-* tokens; works under data-theme="dark" and "light".
 */
interface ValidationResult {
  isUnavailable: boolean;
  numericValue?: number;
}

const validateValue = (
  kind: DataValueKind,
  value: unknown,
): ValidationResult => {
  // Unavailable: explicit kind OR null/undefined value
  if (kind === 'unavailable' || value === null || value === undefined) {
    return { isUnavailable: true };
  }

  if (isNumericKind(kind)) {
    // Guard 1: empty/whitespace string → unavailable
    if (typeof value === 'string' && value.trim() === '') {
      return { isUnavailable: true };
    }

    // Guard 2: coerce and check finite
    const numValue = value as number | string;
    const n = toNum(numValue);
    if (!Number.isFinite(n)) {
      return { isUnavailable: true };
    }

    return { isUnavailable: false, numericValue: n };
  }

  return { isUnavailable: false };
};

export const FmDataValue: React.FC<FmDataValueProps> = ({
  rule,
  locale = 'fi-FI',
  className,
  'data-testid': dataTestId,
  unavailableLabel,
  qualifierAriaLabel,
}) => {
  const {
    kind,
    value,
    unit,
    currency,
    precision,
    directional,
    tone,
    emphasis,
    signDisplay,
  } = rule;

  const validation = validateValue(kind, value);

  if (validation.isUnavailable) {
    return renderUnavailable(className, dataTestId, unavailableLabel);
  }

  const formatted = formatValue(
    kind,
    displayValue(kind, value as string | number),
    { locale, precision, unit, currency },
  );
  const valuePrefix = isNumericKind(kind) && validation.numericValue !== undefined
    ? signPrefix(validation.numericValue as number, signDisplay)
    : '';

  return (
    <span
      className={cx(
        'fm-dv',
        `fm-dv--${kind}`,
        tone && `fm-dv--tone-${tone}`,
        emphasis === 'strong' && 'fm-dv--strong',
        directional && 'fm-dv--directional',
        className,
      )}
      data-testid={dataTestId ?? 'fm-data-value'}
    >
      {directional ? (
        <span
          className="fm-dv__qualifier"
          data-testid="fm-data-value-qualifier"
          {...(qualifierAriaLabel ? { 'aria-label': qualifierAriaLabel } : {})}
        >
          ~
        </span>
      ) : null}
      <span className="fm-dv__value">{valuePrefix}{formatted.text}</span>
      {formatted.unit ? (
        <span className="fm-dv__unit">{formatted.unit}</span>
      ) : null}
    </span>
  );
};

export default FmDataValue;
